import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { HighlightOffRounded } from '@material-ui/icons';
import { PunchListExportSummary } from '../../api-client/autogenerated';
import { getDocumentsState } from '../../features/documents/selectors';
import { getPhysicalBuildingsState } from '../../features/project/selectors';
import { getPunchListExports } from '../../models/api/punch-list-export';
import { parseDate } from '../../scripts/utils';
import { CancelButton } from '../custom-components/CustomButtons';
import ExportLogTable, { ExportLogEntry } from '../admin-dashboard/ExportLogTable';
import PunchListLocationTreeView from './PunchListLocationTreeView';
import {
  allItemIds,
  buildPunchListExportTree,
  PunchListExportTreeLocation,
} from './punch-list-export-utils';

// Matches ExportDocumentTypesDialog and ExportContentsDialog, so the two exports look alike.
const useStyles = makeStyles(() =>
  createStyles({
    titleContainer: {
      display: 'flex',
      flexDirection: 'row',
      height: '50px',
      padding: '8px 16px 8px 16px',
      borderBottom: '1px solid #EDECEC',
      minWidth: 500,
    },
    title: {
      marginTop: '3px',
      marginLeft: '6px',
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '22px',
      lineHeight: '30px',
      textAlign: 'center',
      color: '#0947B9',
      textTransform: 'uppercase',
      justifySelf: 'center',
    },
    rootIconButton: {
      padding: 0,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  }),
);

const formatTimestamp = (value: string) => parseDate(value).format('MM/DD/YYYY h:mm A');

const describeItems = (count: number) => `${count} item${count === 1 ? '' : 's'}`;

type PunchListExportLogEntry = ExportLogEntry & { summary: PunchListExportSummary };

/** What a past export contains, in the tree it was picked with. Nothing here can be changed. */
function PunchListExportContentsDialog(props: {
  summary?: PunchListExportSummary;
  tree: PunchListExportTreeLocation[];
  closeDialog: () => void;
}) {
  const { summary, tree, closeDialog } = props;
  const classes = useStyles();

  return (
    <Dialog open={!!summary} onClose={closeDialog} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.titleContainer}>
        <Typography className={classes.title} align="center">
          Export Contents
        </Typography>
        <IconButton
          id="punch-list-export-contents-dialog-close-button"
          style={{ right: '20px', top: '12px', position: 'absolute' }}
          onClick={closeDialog}
          classes={{ root: classes.rootIconButton }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          style={{ marginTop: 8, marginBottom: 16, fontWeight: 'bolder', color: 'black' }}
        >
          {summary && formatTimestamp(summary.generatedOn)}
        </Typography>
        <Typography style={{ marginBottom: 16 }}>
          Items deleted since this export was generated are no longer listed.
        </Typography>
        <PunchListLocationTreeView
          tree={tree}
          selectedItemIds={summary?.documentIds ?? []}
          // Read-only, so nothing ever calls this.
          setSelectedItemIds={() => {}}
          disabled
        />
      </DialogContent>
      <DialogActions style={{ justifyContent: 'flex-end', padding: 16 }}>
        <CancelButton onClick={closeDialog}>Close</CancelButton>
      </DialogActions>
    </Dialog>
  );
}

type PunchListExportSelectionDialogProps = {
  open: boolean;
  closeDialog: () => void;
  projectId: string;
  /** Rows ticked in the log table, which start out selected here; with none, everything does. */
  initialSelectedItemIds: string[];
  onSubmit: (documentIds: string[]) => void;
};

/**
 * Starts a punch list export: the exports already taken, so a finished one can be picked back up,
 * and the locations and items a new one should contain. Laid out like the project export dialog.
 */
export default function PunchListExportSelectionDialog(props: PunchListExportSelectionDialogProps) {
  const { open, closeDialog, projectId, initialSelectedItemIds, onSubmit } = props;
  const classes = useStyles();

  const documents = useSelector(getDocumentsState);
  const buildings = useSelector(getPhysicalBuildingsState);
  const tree = useMemo(() => buildPunchListExportTree(documents, buildings), [
    documents,
    buildings,
  ]);

  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [exports, setExports] = useState<PunchListExportSummary[]>([]);
  const [lastUpdatedOn, setLastUpdatedOn] = useState<string>();
  const [isLogLoading, setIsLogLoading] = useState(false);
  const [contentsSummary, setContentsSummary] = useState<PunchListExportSummary>();

  // The selection is seeded once per opening, not every time the table's selection changes -- but
  // only once there are items to seed it with: opened from an email link, the dialog can be up
  // before the punch list has loaded.
  const isSeeded = useRef(false);
  const hasItems = tree.length > 0;
  useEffect(() => {
    if (!open) {
      isSeeded.current = false;
      return;
    }
    if (isSeeded.current || !hasItems) return;
    isSeeded.current = true;
    const everyItemId = allItemIds(tree);
    const ticked = initialSelectedItemIds.filter((id) => everyItemId.includes(id));
    setSelectedItemIds(ticked.length > 0 ? ticked : everyItemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasItems]);

  useEffect(() => {
    if (!open) return undefined;
    let isCurrent = true;
    setIsLogLoading(true);
    getPunchListExports(projectId)
      .then((log) => {
        if (!isCurrent) return;
        setExports(log.exports);
        setLastUpdatedOn(log.lastUpdatedOn);
      })
      // The log is a convenience; failing to load it should not block starting an export.
      .catch(() => {
        if (isCurrent) setExports([]);
      })
      .finally(() => {
        if (isCurrent) setIsLogLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [open, projectId]);

  const entries = useMemo<PunchListExportLogEntry[]>(
    () =>
      exports.map((summary) => ({
        id: summary.jobStatusId,
        generatedOn: summary.generatedOn,
        generatedByUserName: summary.generatedByUserName,
        status: summary.status,
        percentComplete: summary.percentComplete,
        fileId: summary.fileId,
        error: summary.error,
        isCurrent: summary.isCurrent,
        summary,
      })),
    [exports],
  );

  return (
    <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
      <DialogTitle className={classes.titleContainer}>
        <Typography className={classes.title} align="center">
          Export Punch List
        </Typography>
        <IconButton
          id="punch-list-export-selection-dialog-close-button"
          style={{ right: '20px', top: '12px', position: 'absolute' }}
          onClick={closeDialog}
          classes={{ root: classes.rootIconButton }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          style={{ fontWeight: 'bolder', color: 'black', marginTop: 8, marginBottom: 16 }}
        >
          {lastUpdatedOn
            ? `Punch List last updated on ${formatTimestamp(lastUpdatedOn)}`
            : isLogLoading
            ? ' '
            : 'This punch list has no items yet.'}
        </Typography>

        <Typography style={{ fontWeight: 600, marginTop: 8, marginBottom: 8 }}>
          Export Log
        </Typography>
        <ExportLogTable
          entries={entries}
          isLoading={isLogLoading}
          emptyMessage="You have not exported this punch list yet."
          staleTooltip="Not current. The punch list changed after this export."
          describeContents={({ summary }) =>
            describeItems(summary.itemCount ?? summary.documentIds.length)
          }
          onShowContents={({ summary }) => setContentsSummary(summary)}
        />

        <Typography style={{ fontWeight: 600, marginTop: 24, marginBottom: 8 }}>
          New export
        </Typography>
        <Typography style={{ marginBottom: 16 }}>
          Select the locations and items to include. Exports keep running after this window is
          closed, and you will get a notification when yours is ready.
        </Typography>
        <PunchListLocationTreeView
          tree={tree}
          selectedItemIds={selectedItemIds}
          setSelectedItemIds={setSelectedItemIds}
        />
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between', padding: 16 }}>
        <CancelButton onClick={closeDialog}>Cancel</CancelButton>
        <Button
          color="primary"
          variant="contained"
          onClick={() => onSubmit(selectedItemIds)}
          disabled={selectedItemIds.length === 0}
        >
          Export
        </Button>
      </DialogActions>

      <PunchListExportContentsDialog
        summary={contentsSummary}
        tree={tree}
        closeDialog={() => setContentsSummary(undefined)}
      />
    </Dialog>
  );
}
